"use client";

import { ContentLink } from "@components/cms/link/ContentLink";
import { AnalyticsClient } from "@lib/analytics/AnalyticsClient";
import { CommonCMS } from "@lib/constants/contentful";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import type { LinkModel } from "@lib/contentful/mappers/parts/link/types";
import type { StoryTileModel } from "@lib/contentful/mappers/parts/storyTile/types";
import type { StoryTileGridModel } from "@lib/contentful/mappers/modules/storyTileGrid/types";
import { eccoClubGoldGradient } from "@ecco/ui/tokens/colors";
import { StoryGrid } from "@ecco/ui/story-grid";
import {
    normalizeGridBackground,
    resolveGridBackgroundStyles,
} from "@ecco/ui/grid-background-styles";
import { StoryTile, type StoryTileGridBackground } from "@ecco/ui/story-tile";
import { StoryTileCarousel } from "./components/StoryTileCarousel";
import { Button } from "@ecco/ui/button";
import { Box } from "@ecco/ui/box";
import { Flex } from "@ecco/ui/flex";
import { NextImage } from "@ecco/ui/next-image";
import type { FC } from "react";
import { BenefitsGrid } from "./components/BenefitsGrid";
import { OverlayStoryList } from "./components/OverlayStoryList";
import { styles } from "./StoryTileGridStyles";
import { resolveDisplayMode } from "./utils";

const COLORED_BAR_TOKEN_MAP = {
    Green: "eccoClub.turquoise",
    Blue: "eccoClub.blue",
    Purple: "eccoClub.purple",
    Gold: eccoClubGoldGradient,
} as const;

interface IGridStyles {
    wrapperBg?: string;
    wrapperPy: number | number[];
    textColor: string;
}

const DEFAULT_GRID_STYLES: IGridStyles = { wrapperBg: undefined, wrapperPy: 0, textColor: "black" };

const GRID_STYLES_BY_BG: Partial<Record<StoryTileGridBackground, IGridStyles>> = {
    White: DEFAULT_GRID_STYLES,
    Gray: { wrapperBg: "gray.25", wrapperPy: [6, 8], textColor: "black" },
    Black: { wrapperBg: "black", wrapperPy: 0, textColor: "white" },
};

const LinksWrapper = ({ links, isCarousel }: { links: LinkModel[]; isCarousel: boolean }) => (
    <Flex {...(isCarousel ? styles.linksWrapperCarousel : styles.linksWrapperGrid)}>
        {links.map((link) => (
            <Button
                key={link.id}
                {...(isCarousel ? styles.linkButtonCarousel : styles.linkButtonGrid)}
                as={ContentLink}
                link={link}
                variant={"link"}
            >
                <span>{link.label}</span>
            </Button>
        ))}
    </Flex>
);

export const StoryTileGrid: FC<StoryTileGridModel> = ({
    id: gridId,
    title,
    titleSize = "large",
    description,
    storyTiles,
    backgroundColor = "White",
    displayMode,
    callToAction,
}) => {
    const { getMultiple } = useMicrocopy();
    const microcopies = getMultiple(CommonCMS.shared, [
        CommonCMS.generic.readMore,
        CommonCMS.generic.readLess,
    ]);

    const tileGridBackground = normalizeGridBackground(GRID_STYLES_BY_BG, backgroundColor);
    const { textColor, ...gridStyles } = resolveGridBackgroundStyles(
        GRID_STYLES_BY_BG,
        DEFAULT_GRID_STYLES,
        tileGridBackground
    );

    const tileCount = storyTiles.length;
    const mode = resolveDisplayMode(displayMode, tileCount);

    const isCarousel = mode === "carousel";

    const renderImageOnlyTile = (
        key: string,
        imageSrc: string | undefined,
        imageAlt: string,
        imageSizes: string[],
        whiteBackground: boolean
    ) => (
        <Box
            key={key}
            __css={styles.imageOnlyTile}
            display={whiteBackground ? { base: "none", xl: "block" } : "block"}
        >
            <Box __css={styles.imageOnlyInner}>
                {whiteBackground ? (
                    <Box bg="white" w="full" h="full" />
                ) : (
                    <NextImage
                        src={imageSrc}
                        alt={imageAlt}
                        sizes={imageSizes}
                        fill
                        objectFit="cover"
                    />
                )}
            </Box>
        </Box>
    );

    const renderTile = (storyTile: StoryTileModel, i: number, isCarousel: boolean = false) => {
        const {
            id,
            links = [],
            image,
            label,
            description: tileDescription,
            headline,
            whiteBackground = false,
            activateReadMore = true,
            eccoClubBenefits = [],
            coloredBar,
        } = storyTile;
        const imageSrc = image?.url;
        const imageAlt = image?.alt ?? "";
        const coloredBarToken = coloredBar ? COLORED_BAR_TOKEN_MAP[coloredBar] : undefined;
        const hasTextContent = Boolean(headline || tileDescription || label) || links.length > 0;

        if (eccoClubBenefits.length) {
            return (
                <BenefitsGrid
                    key={`${id}-${i}`}
                    title={headline}
                    description={tileDescription}
                    benefits={eccoClubBenefits}
                    links={<LinksWrapper links={links} isCarousel={isCarousel} />}
                />
            );
        }

        if (hasTextContent) {
            return (
                <StoryTile
                    key={`${id}-${i}`}
                    imageSrc={imageSrc}
                    imageAlt={imageAlt}
                    label={label}
                    headline={headline}
                    description={tileDescription}
                    onInView={() =>
                        AnalyticsClient.viewPromotion({
                            image: imageSrc,
                            title: headline,
                            index: i,
                            id: gridId + id,
                        })
                    }
                    links={<LinksWrapper links={links} isCarousel={isCarousel} />}
                    whiteBackground={whiteBackground}
                    activateReadMore={coloredBarToken ? false : activateReadMore}
                    showMoreLabel={microcopies?.[CommonCMS.generic.readMore]}
                    showLessLabel={microcopies?.[CommonCMS.generic.readLess]}
                    coloredBar={coloredBarToken}
                    gridBackground={tileGridBackground}
                    isCarousel={isCarousel}
                    tileCount={isCarousel ? undefined : tileCount}
                />
            );
        }

        if (isCarousel) {
            return renderImageOnlyTile(
                `${id}-${i}`,
                imageSrc,
                imageAlt || "",
                ["85vw", "30vw"],
                whiteBackground
            );
        }

        if (mode === "grid") {
            const imageSizes = tileCount === 2 ? ["100vw", "50vw"] : ["100vw", "33vw"];
            return renderImageOnlyTile(
                `${id}-${i}`,
                imageSrc,
                imageAlt || "",
                imageSizes,
                whiteBackground
            );
        }

        return (
            <Box key={`${id}-${i}`} __css={styles.imageOnlyFallback}>
                {whiteBackground ? (
                    <Box bg="white" __css={styles.imageOnlyFallbackFill} />
                ) : (
                    <NextImage
                        src={imageSrc}
                        alt={imageAlt || ""}
                        sizes={["100vw", "100vw"]}
                        fill
                        objectFit="cover"
                    />
                )}
            </Box>
        );
    };

    if (isCarousel) {
        return (
            <Box
                {...styles.moduleCarousel}
                py={gridStyles.wrapperPy}
                bg={gridStyles.wrapperBg}
                data-testid="story-tile-carousel-module"
            >
                <StoryTileCarousel title={title} titleSize={titleSize}>
                    {storyTiles.map((tile, i) => renderTile(tile, i, true))}
                </StoryTileCarousel>
            </Box>
        );
    }

    return (
        <Box
            {...styles.moduleGrid}
            {...(mode === "grid" ? styles.moduleGridPadding : { py: gridStyles.wrapperPy })}
            bg={gridStyles.wrapperBg}
            data-testid="story-tile-grid-module"
        >
            {mode === "overlay" ? (
                <OverlayStoryList
                    title={title}
                    storyTiles={storyTiles}
                    onTileInView={(storyTile, i) => {
                        AnalyticsClient.viewPromotion({
                            image: storyTile.image?.url,
                            title: storyTile.headline,
                            index: i,
                            id: "story-tile-" + storyTile.title,
                        });
                    }}
                    textColor={textColor}
                />
            ) : (
                <StoryGrid
                    tileCount={tileCount}
                    title={title}
                    titleSize={titleSize}
                    description={description}
                    textColor={textColor}
                >
                    {storyTiles.map((tile, i) => renderTile(tile, i))}
                </StoryGrid>
            )}
            {callToAction && (
                <Flex justifyContent="center" pb={[6, 8]}>
                    <Button
                        as={ContentLink}
                        link={callToAction.link}
                        variant="standard"
                        colorScheme={callToAction.variant === "solid" ? "black" : "white"}
                    >
                        {callToAction.label}
                    </Button>
                </Flex>
            )}
        </Box>
    );
};
